<template>
  <v-card color="white" flat class="pa-1 position-relative py-3">
    <div class="d-flex justify-space-between px-3 py-3">
      <div class="text-h5"><b>Cuotas Lote:</b> {{ lotData.LotName }}</div>
       <Button 
          icon="mdi-close"
          color="grey"
          size="small" 
          @click="$emit('close')"
        >
        </Button>
    </div>
    <div class="d-flex justify-end">
      <Button 
        prepend-icon="mdi-cash-sync"
        color="warning"
        size="small" 
        class="mr-3"
        @click="() =>  showFinancingDialog = true"
      >
        Refinanciar
      </Button>
      <Button 
        prepend-icon="mdi-cash-plus"
        color="success"
        size="small" 
        class="mr-3"
        disabled
      >
      Ingresar pago general
      </Button>
      <Button 
        prepend-icon="mdi-invoice-list"
        size="small" 
        class="mr-3"
        @click="selectedInstallment = 'all'"
      >
      Historial de comprobantes
      </Button>
    </div>
    <Block v-if="isLoading" />
    <div
      style="max-height: 600px; overflow: auto;"
    >
      <div
        v-for="(plan, idx) in plans"
        :key="plan.ID_FinancingPlan"
        class="pt-3"
      >
        <div class="d-flex justify-space-between px-4 py-3">
          <div class="d-flex" style="color: #37474F;">
            <div class="mr-3" style="font-weight: bold;">
              Plan de financiamento {{ plans.length - idx }}
            </div>
            <div v-if="plan.Status === enums.FinancingPlanStatus.ACTIVE">
              {{ moment(plan.StartDate).format('DD/MM/YYYY') }}
            </div>
            <div v-else>
              {{ moment(plan.StartDate).format('DD/MM/YYYY') }} - {{ moment(plan.EndDate).format('DD/MM/YYYY') }}
            </div>
          </div>
          <div
            :style="{
              color: getPlanStatusColor(plan.Status),
              fontWeight: 'bold',
            }"
          >
            {{ getPlanStatusName(plan.Status) }}
          </div>
          <div 
            v-if="plan.Status === enums.FinancingPlanStatus.ACTIVE"
            class="d-flex"
          >
            <div class="pr-3">Balance actual: </div>
            <div
              :style="{
              color: plan.balance.TotalBalance <= 0 
                  ? '#43A047'
                  : '#E57373', 
              }"
            >
              ${{ parseFloat(plan.balance.TotalBalance) }}
            </div>
          </div>
        </div>
        <DxDataGrid
          :dataSource="plan.installments"
          :showBorders="true"
          :show-row-lines="false"
          ref="dataGrid"
          :scrolling="{ useNative: true }"
          :paging="{ enabled: false }"
          :column-auto-width="true"
        >
          <DxColumn cell-template="actions-template" />
          <template #actions-template="{ data }">
            <div class="row-actions d-flex justify-center align-center" >
              <Button 
                v-if="!isInstallmentPaid(data.data)"
                icon="mdi-cash-plus" 
                size="small"
                color="success" 
                class="mr-1"
                @click="$emit('row-selected', {
                  row: data.data,
                  saleData,
                  planData: plan,
                })"
              >
                Procesar pago
              </Button>
              <Button 
                v-if="hasBilling(data.data)"
                icon="mdi-invoice-list" 
                size="small" 
                @click="selectedInstallment = data.data"
              >
              </Button>
            </div>
          </template>
    
          <DxColumn dataField="ID_FinancingInstallment" caption="ID de cuota" />
    
          <DxColumn dataField="InstallmentNumber" caption="Número de cuota" />
    
          <DxColumn dataField="PeriodName" caption="Mes" />
    
          <DxColumn dataField="BaseAmount" caption="Cuota base" :format="(value) => `$${parseFloat(value)}`"/>
    
          <DxColumn dataField="InterestAmount" caption="Cuota Interés" :format="(value) => `$${parseFloat(value)}`"/>
          
          <DxColumn 
            dataField="BaseBalance" 
            caption="Balance base"
            cell-template="base-balance-template"
          />
          <template #base-balance-template="{ data: row }">
            <div
              :style="{
                fontWeight: 'bold',
                color: row.data.BaseBalance > 0 ? '#EF5350' : '#A5D6A7',
              }"
            >
              {{ `${currency}${parseFloat(row.data.BaseBalance)}` }}
            </div>
          </template>
    
          <DxColumn 
            dataField="InterestBalance" 
            caption="Balance interés"
            cell-template="interest-balance-template"
          />
          <template #interest-balance-template="{ data: row }">
            <div
              :style="{
                fontWeight: 'bold',
                color: row.data.InterestBalance > 0 ? '#EF5350' : '#A5D6A7',
              }"
            >
              {{ `${currency}${parseFloat(row.data.InterestBalance)}` }}
            </div>
          </template>
    
        </DxDataGrid>
      </div>
    </div>

    <v-dialog 
      v-model="showBillingTable" 
      @close="selectedInstallment = null"
      @click:outside="selectedInstallment = null"
      max-width="1000px"
      max-height="90vh"
      scrollable
    >
      <InstallmentBillingTable 
        v-if="selectedInstallment" 
        :lot-data="lotData"
        :sale-data="saleData"
        :billing-data="billing"
        :installment-data="selectedInstallment"
        :installments-data="installments"
        @row-selected="selectedInstallment = null"
        @close="selectedInstallment = null"
      />
    </v-dialog>

    <v-dialog 
      v-model="showFinancingDialog" 
      @close="showFinancingDialog = false"
      @click:outside="showFinancingDialog = false"
      max-width="1000px"
      max-height="90vh"
      scrollable
    >
      <RealStateLotForm 
        v-if="lotData" 
        :lot-data="lotData"
        @close="showFinancingDialog = false"
        @lot-refinanced="() => {
          $emit('lot-refinanced');
          showFinancingDialog = false;
        }"
        refinancing
      />
    </v-dialog>

    <Alert
      v-model="alert.show"
      :type="alert.type"
      :title="alert.title"
      :message="alert.message"
      @input="alert.onInputEvent"
      @action="alert.onActionEvent"
    />
  </v-card >
</template>

<script>
import Block from '@/components/Block.vue';
import { DxDataGrid, DxColumn } from "devextreme-vue/data-grid";
import Button from '@/components/Button.vue';
import Alert from '@/components/Alert.vue';
import InstallmentBillingTable from './InstallmentBillingTable.vue';
import RealStateLotForm from '../../../inventory/mainPages/real-state/RealStateLotForm.vue';
import enums from '@/helpers/enums.js';
import moment from 'moment';

export default {
  name: 'LotInstallments',
  components: {
    Block,
    DxDataGrid,
    DxColumn,
    Button,
    Alert,
    InstallmentBillingTable,
    RealStateLotForm,
  },
  props: {
    lotData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      alert: {
        type: 'success',
        show: false,
        header: '',
        body: '',
        onInputEvent: this.closeAlert,
        onActionEvent: this.closeAlert,
      },
      currency: '$',
      saleData: null,
      plans: [],
      installments: [],
      billing: [],
      selectedInstallment: null,
      showFinancingDialog: false,
      enums,
      moment,
    };
  },
  computed: {
    showBillingTable() {
      return !!this.selectedInstallment
    },
    currentPlan() {
      if (!this.plans || this.plans.length <= 0) {
        return null;
      }

      return this.plans[this.plans.length - 1];
    },
  },
  watch: {
    lotData () {
      this.reload();
    }
  },  
  methods: {
    async loadInstallments() {
      this.isLoading = true;
      try {
        const response = await this.$API.realstate.getLotFinancing(this.lotData.ID_RealStateLot);

        if (!response?.data) {
          this.saleData = null;
          this.plans = [];
          this.installments = [];
          return
        }

        const { data: resData } = response;

        this.saleData = resData;

        if (!resData.plans || resData.plans.length <= 0) {
          this.installments = [];
          this.plans = [];
          return
        }

        this.plans = resData.plans;

        this.installments = this.plans[this.plans.length - 1].installments;

      } catch(err) {
        console.log(err)
        this.showAlert('Error', 'Ha ocurrido obteniendo la lista de cuotas.', 'error');
      }

      this.isLoading = false;
    },
    async loadLotBilling() {
      this.isLoading = true;
      try {
        const ID_Business = JSON.parse(localStorage.getItem("user")).businessid
        const ID_Branch = JSON.parse(localStorage.getItem("user")).branch

        const search = {
          ID_Business,
          ID_Branch,
          ID_RealStateLot: this.lotData.ID_RealStateLot
        };

        const response = await this.$API.realstate.getLotBilling({
          search
        });

        this.billing = response.data;
      } catch(err) {
        console.log(err)
        this.showAlert('Error', 'Ha ocurrido obteniendo la lista de facturas.', 'error');
      }

      this.isLoading = false;
    },
    getInstallmentTotal(installment) {
      if (!installment) {
        return 0;
      }

      return installment.BaseAmount + installment.InterestAmount;
    },  
    isInstallmentPaid(installment) {
      if (!installment) {
        return false
      }

      const totalBalance = parseFloat(installment.BaseBalance) + parseFloat(installment.InterestBalance);

      return totalBalance <= 0;
    },
    reload() {
      this.loadInstallments();
      this.loadLotBilling();
    },
    hasBilling(installment) {
      const invoices = this.billing.filter(b => {
        return `${b.ID_RealStateInstallment}` === `${installment.ID_FinancingInstallment}`
      })

      return invoices.length > 0;
    },
    getPlanStatusName(planStatus) {
      switch (planStatus) {
        case enums.FinancingPlanStatus.INACTIVE:
          return 'ACTIVO';
        case enums.FinancingPlanStatus.ACTIVE:
          return 'ACTIVO';
        case enums.FinancingPlanStatus.PAID:
          return 'PAGADO';
        case enums.FinancingPlanStatus.REFINANCED:
          return 'REFINANCIADO';
        case enums.FinancingPlanStatus.CANCELLED:
          return 'CANCELADO';
        default:
          return '';
      }
    },
    getPlanStatusColor(planStatus) {
      switch (planStatus) {
        case enums.FinancingPlanStatus.INACTIVE:
          return '#0000008a';
        case enums.FinancingPlanStatus.ACTIVE:
          return '#00695C';
        case enums.FinancingPlanStatus.PAID:
          return '#2E7D32';
        case enums.FinancingPlanStatus.REFINANCED:
          //return '#4527A0';
          return '#0000008a';
        case enums.FinancingPlanStatus.CANCELLED:
          return '#0000008a';
        default:
          return '';
      }
    },
    showAlert(title, message, type = 'success', onInputEvent, onActionEvent) {
      this.alert.type = type;
      this.alert.title = title;
      this.alert.message = message;
      this.alert.show = true;
      this.alert.onInputEvent = onInputEvent 
        ? onInputEvent
        : this.closeAlert;
      this.alert.onActionEvent = onActionEvent 
        ? onActionEvent
        : this.closeAlert;
      
    },
    closeAlert() {
      this.alert.show = false;
    },
  },
  mounted() {
    this.reload();
    this.currency = JSON.parse(localStorage.getItem("branch")).Currency;
  }
}
</script>
